import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';

/**
 * Banner de onboarding TENANT-LEVEL (setup da clínica).
 *
 * Diferente do OnboardingBanner existente (per-user pós-aceite de
 * convite), este aparece SÓ pro owner enquanto a clínica está
 * configurando: cadastrar serviços, dentistas, pacientes, integrações.
 *
 * Mostra progresso visual + checklist clicável. Esconde quando todos
 * os steps required estão done.
 */

interface Step {
  id: string;
  label: string;
  description: string;
  done: boolean;
  required: boolean;
  count?: number;
  link: string;
}

interface OnboardingStatus {
  complete: boolean;
  progress: { done: number; total: number; percentage: number };
  steps: Step[];
}

const DISMISS_KEY = 'tenant-onboarding-dismissed';

export function TenantOnboardingBanner() {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(true);
  // Dismiss é POR TENANT — testar/usar várias clínicas no mesmo
  // navegador não deve esconder o banner das outras.
  const dismissKey = user?.tenantId
    ? `${DISMISS_KEY}.${user.tenantId}`
    : DISMISS_KEY;
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(dismissKey) === '1',
  );

  const isE2E =
    typeof navigator !== 'undefined' && navigator.webdriver === true;
  const canSee = user?.role === 'owner';

  const { data } = useQuery<OnboardingStatus>({
    queryKey: ['tenant-onboarding'],
    queryFn: async () => (await api.get('/tenants/me/onboarding')).data,
    refetchOnWindowFocus: !isE2E,
    // staleTime curto + refetch quando volta foco. Antes era 60s, mas
    // o banner não desmonta entre navegações — se um flow esquecia de
    // invalidar (ex.: cadastrar dentista em /equipe), o checklist
    // ficava preso "pendente" por 1 min. Reduzir pra 10s preserva
    // 99% do benefício de cache sem essa UX ruim.
    staleTime: 10_000,
    // refetch toda vez que React Router monta uma rota (navegação
    // entre /equipe → /painel) — garantia adicional além do invalidate
    // explícito nos flows.
    refetchOnMount: true,
    retry: 1,
    enabled: canSee && !isE2E,
  });

  if (!canSee || isE2E || dismissed) return null;
  if (!data || data.complete) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  const { progress, steps } = data;
  const requiredPending = steps.filter((s) => s.required && !s.done);

  return (
    <div
      className="bg-gradient-to-r from-teal-50 to-emerald-50 border-b-2 border-teal-200"
      data-testid="tenant-onboarding-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="text-3xl" aria-hidden>
              🚀
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-teal-900">
                Configure sua clínica pra começar a vender
              </p>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-2 w-32 bg-teal-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-600 transition-all"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <p className="text-xs text-teal-700 font-medium">
                  {progress.done} de {progress.total} —{' '}
                  {requiredPending.length === 0
                    ? 'tudo pronto!'
                    : `falta ${requiredPending.length}`}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="px-3 py-1.5 text-xs rounded-md bg-teal-600 text-white hover:bg-teal-700 font-medium"
            >
              {expanded ? 'Ocultar passos' : 'Ver checklist'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="px-2 py-1.5 text-xs text-teal-700 hover:text-teal-900"
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>

        {expanded && (
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {steps.map((step) => (
              <li key={step.id}>
                <Link
                  to={step.link}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg border transition ${
                    step.done
                      ? 'border-emerald-200 bg-emerald-50/50'
                      : 'border-ink-200 bg-white hover:border-teal-300 hover:shadow-sm'
                  }`}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <span className="text-base shrink-0 mt-0.5">
                    {step.done ? '✓' : step.required ? '○' : '◇'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-xs font-medium ${
                        step.done
                          ? 'text-emerald-800 line-through opacity-75'
                          : 'text-ink-800'
                      }`}
                    >
                      {step.label}
                      {!step.required && (
                        <span className="ml-1 text-[9px] text-ink-500 uppercase">
                          opcional
                        </span>
                      )}
                      {typeof step.count === 'number' && step.count > 0 && (
                        <span className="ml-1 text-[10px] text-teal-700">
                          ({step.count})
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-ink-500 mt-0.5">
                      {step.description}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
